import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignIn as ClerkSignIn, useAuth, useClerk } from '@clerk/clerk-react';
import Logo2 from '../assets/Logo2.png';

const STUDENT_URL = import.meta.env.VITE_STUDENT_URL || '';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Sign-in surface for Platform-Admin. Renders Clerk's in-app `<SignIn />`
 * (no sign-up link — admins are provisioned manually via the Clerk
 * Dashboard with `public_metadata.role = "ADMIN"` set).
 *
 * Once the user is signed in, we hit `/v1/students/profile/me` to confirm
 * their server-side role is ADMIN. Non-admins (a student who somehow ended
 * up on this URL) get signed out and bounced to the Student app's portal
 * picker — same UX as the previous Logto callback flow.
 */
export default function SignIn() {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const handled = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || handled.current) return;
    handled.current = true;

    void (async () => {
      try {
        // Use Clerk's getToken directly rather than the module-level
        // apiFetch wrapper: apiFetch only attaches a Bearer token once
        // AuthTokenSetter's effect (App.tsx) has wired it up, and that
        // sibling effect can commit after this one. An unauthenticated
        // /profile/me 401s, the code below falls through to navigate(),
        // the dashboard's first call 401s → signOut → back to /signin
        // → the sign-in form "reappears". Tokenizing here breaks that loop.
        const token = await getToken();
        const profileRes = await fetch(`${API_BASE_URL}/v1/students/profile/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (profileRes.ok) {
          const profile = await profileRes.json();
          const role = profile?.role;
          if (role && role !== 'ADMIN') {
            // Wrong portal. Sign out and return to OUR sign-in with a flag so
            // we can explain why. Previously this bounced silently to the
            // student app, which admins testing the wrong account reported as
            // a broken login ("kicked back to sign-in with no message").
            await signOut({ redirectUrl: '/signin?denied=1' });
            return;
          }
          // Admin (or role missing — let the API enforce on the next call).
          navigate('/manage-schools', { replace: true });
          return;
        }
        // Profile fetch failed but auth succeeded — proceed; downstream API
        // calls will surface the real problem with a useful error.
        navigate('/manage-schools', { replace: true });
      } catch (err) {
        console.error('[SignIn] role check failed:', err);
        setErrorMessage(err instanceof Error ? err.message : 'Sign-in completed but role check failed.');
        handled.current = false;
      }
    })();
  }, [isLoaded, isSignedIn, navigate, signOut, getToken]);

  // Show a neutral spinner while Clerk is still booting OR once signed in
  // and confirming role. Critically, we do NOT render <ClerkSignIn> until
  // Clerk is loaded — otherwise a returning admin who already has a session
  // sees the sign-in form flash for the duration of the Clerk boot before
  // it's swapped for the redirect, which is the visible login flicker.
  if (!isLoaded || isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-gray-600">{isSignedIn ? 'Verifying access…' : 'Loading…'}</p>
          {errorMessage ? <p className="mt-2 text-red-600 text-sm">{errorMessage}</p> : null}
        </div>
      </div>
    );
  }

  // Set when a signed-in account without the ADMIN role was bounced back here
  // (see the wrong-portal branch above). Explains the "kicked to sign-in"
  // behavior instead of leaving the user guessing.
  const deniedAccess = new URLSearchParams(window.location.search).get('denied') === '1';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-12 gap-6">
      <img className="w-52 h-10" src={Logo2} alt="Platform Admin" />
      {deniedAccess ? (
        <div className="w-full max-w-md rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700">
          That account doesn't have access to <span className="font-semibold">Hypebook Admin</span>. Sign in with an admin account
          {STUDENT_URL ? (
            <>
              {' '}
              — or{' '}
              <a href={`${STUDENT_URL}/sign-in`} className="font-medium underline">
                open the student app
              </a>
            </>
          ) : null}
          .
        </div>
      ) : null}
      {/* routing="virtual" — keeps the whole flow at /signin without URL
          navigation between steps. routing="path" navigated to
          /signin/factor-one for the OTP step; that URL change (under the
          /signin/* wildcard route in App.tsx) re-mounted this page and
          re-fired prepareFirstFactor, sending a SECOND verification email.
          Same fix already applied to School-Admin (d32ad18). */}
      <ClerkSignIn
        routing="virtual"
        // No sign-up surface for Platform-Admin. Hide the footer that
        // links to it; admin accounts are provisioned manually.
        appearance={{
          elements: {
            footer: { display: 'none' },
            footerAction: { display: 'none' },
            // Hide the Clerk card's built-in logo — it renders the Clerk
            // instance's (stale, cloned-from-dev) logo, and this page already
            // shows the current wordmark (Logo2) above the card. Prevents the
            // old + new logos appearing together.
            logoBox: { display: 'none' },
          },
        }}
      />
    </div>
  );
}
